'use client';

import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, Stack } from '@mui/material';
import type { AuthMethod } from '@/stores/authStore';
import { canSignOutOfIdp } from '@/lib/auth/signOutFlow';
import type { LogoutScope } from '@/lib/auth/signOutFlow';

interface SignOutDialogProps {
  open: boolean;
  onClose: () => void;
  authMethod?: AuthMethod;
  onConfirm: (scope: LogoutScope) => void;
  isSubmitting?: boolean;
}

export function SignOutDialog({ open, onClose, authMethod, onConfirm, isSubmitting = false }: SignOutDialogProps) {
  const idpSignOutAvailable = canSignOutOfIdp(authMethod);

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      aria-labelledby="sign-out-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="sign-out-dialog-title">Sign out of LiST?</DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Choose whether to leave other Penn State or InCommon applications signed in.
          </Typography>

          <Stack spacing={0.5}>
            <Typography variant="subtitle2">View public LiST</Typography>
            <Typography variant="body2" color="text.secondary">
              End your LiST session and browse as a community user. Single sign-on stays active in other applications,
              so you can return to LiST quickly.
            </Typography>
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="subtitle2">Sign out completely</Typography>
            <Typography variant="body2" color="text.secondary">
              End LiST and your single sign-on session. Use this on a shared computer or to switch accounts.
              {idpSignOutAvailable
                ? ' You may need to sign in again in other applications.'
                : ' Full SSO sign-out is available after your next sign-in refreshes this session.'}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
        <Button onClick={onClose} color="secondary" variant="outlined" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={() => onConfirm('list')} color="primary" variant="outlined" disabled={isSubmitting}>
          View public LiST
        </Button>
        <Button
          onClick={() => onConfirm('full')}
          color="primary"
          variant="contained"
          disabled={isSubmitting || !idpSignOutAvailable}
        >
          Sign out completely
        </Button>
      </DialogActions>
    </Dialog>
  );
}
